import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../css/Dashboard.css';
import { menuItems } from './Data';
import { useLoginContext } from './LoginContext';
import { domain } from './Domain';
import useApiRequest from './useApiRequest';
import Profile from './Profile';
import Header from './Header';
import Footer from './Footer';
import ProductForm from './ProductForm';
import InquiryList from './Inquiries';
import OtherActions from './OtherActions';
import { useActivePage } from './ActivePageContext';

const AUTH_INITIAL = {
    id: null,
    token: null,
    name: null,
    date: null,
    image: null,
    role: null,
    is_super: null,
    user_type: null,
};

const MAIN_NAV = [
    { id: 'overview', panel: null, label: 'Overview' },
    { id: 'products', panel: 0, label: 'Add products' },
    { id: 'enquiries', panel: 1, label: 'Enquiries' },
    { id: 'actions', panel: 2, label: 'Other actions' },
    { id: 'profile', panel: 6, label: 'My profile' },
];

const LandingPage = () => {
    return (
        <div className="landing-page">
            <header>
                <div className="container">
                    <h1>Industrial Parts admin console</h1>
                    <p>
                        Manage inventory, product listings, and customer
                        enquiries from one place.
                    </p>
                </div>
            </header>
            <main>
                <section className="about">
                    <div className="container">
                        <p>
                            This console is built around your stock record and
                            inquiry workflow: add or update products, review
                            enquiries, and run day-to-day operations without
                            leaving the dashboard.
                        </p>
                        <p>
                            Use the sidebar to open each area. Your session stays
                            active until you sign out.
                        </p>
                    </div>
                </section>
                <section className="contact">
                    <div className="container">
                        <h2>Need help?</h2>
                        <p>
                            For access issues or training, contact your site
                            administrator or the team that manages this deployment.
                        </p>
                    </div>
                </section>
            </main>
        </div>
    );
};

const Dashboard = () => {
    const { postRequest } = useApiRequest();
    const { setActivePage } = useActivePage();
    const navigate = useNavigate();
    const { userInfo, setUserInfo, setIsLoggedIn } = useLoginContext();
    const [active, setActive] = useState(null);

    const setPanel = useCallback((panel) => {
        setActive(panel);
    }, []);

    const avatarSrc = useMemo(() => {
        if (!userInfo?.image) return null;
        const img = String(userInfo.image);
        if (img.startsWith('http://') || img.startsWith('https://')) return img;
        return domain + img;
    }, [userInfo?.image]);

    const joinedLabel = useMemo(() => {
        if (!userInfo?.date) return null;
        const d = new Date(userInfo.date);
        return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
    }, [userInfo?.date]);

    const handleLogOut = async () => {
        const res = await postRequest(`${domain}/accounts/logout`, {});
        if (res?.message === 'logout successfull') {
            setUserInfo(AUTH_INITIAL);
            localStorage.setItem('isLoggedIn', false);
            setIsLoggedIn(false);
            navigate('/');
        }
    };

    useEffect(() => {
        setActivePage('dashboard');
    }, [setActivePage]);

    return (
        <div className="admin-dashboard-page">
            <Header />
            <div className="admin-dashboard-root">
                <aside className="admin-dashboard-sidebar" aria-label="Admin menu">
                    <div className="admin-dashboard-user">
                        <div className="admin-dashboard-avatar-wrap">
                            {avatarSrc ? (
                                <img
                                    src={avatarSrc}
                                    alt=""
                                    className="admin-dashboard-avatar"
                                />
                            ) : (
                                <div
                                    className="admin-dashboard-avatar admin-dashboard-avatar--placeholder"
                                    aria-hidden
                                >
                                    <i className="fa-solid fa-user" />
                                </div>
                            )}
                        </div>
                        <p className="admin-dashboard-name">{userInfo?.name || 'User'}</p>
                        {joinedLabel && (
                            <p className="admin-dashboard-meta">
                                Member since {joinedLabel}
                            </p>
                        )}
                    </div>

                    <nav className="admin-dashboard-nav" aria-label="Primary">
                        {userInfo?.role === 'Lecturer' &&
                            menuItems.map((item, index) => (
                                <button
                                    key={`lec-${item.title}`}
                                    type="button"
                                    className={`admin-nav-btn${
                                        active === index ? ' admin-nav-btn--active' : ''
                                    }`}
                                    onClick={() => setPanel(index)}
                                >
                                    {item.title}
                                </button>
                            ))}
                        {userInfo?.role === 'Student' && (
                            <>
                                <button
                                    type="button"
                                    className={`admin-nav-btn${
                                        active === 4 ? ' admin-nav-btn--active' : ''
                                    }`}
                                    onClick={() => setPanel(4)}
                                >
                                    Notifications
                                </button>
                                <button
                                    type="button"
                                    className={`admin-nav-btn${
                                        active === 4 ? ' admin-nav-btn--active' : ''
                                    }`}
                                    onClick={() => setPanel(4)}
                                >
                                    Exam results
                                </button>
                                <button
                                    type="button"
                                    className={`admin-nav-btn${
                                        active === 5 ? ' admin-nav-btn--active' : ''
                                    }`}
                                    onClick={() => setPanel(5)}
                                >
                                    Attempt exam
                                </button>
                            </>
                        )}

                        {MAIN_NAV.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`admin-nav-btn${
                                    active === item.panel
                                        ? ' admin-nav-btn--active'
                                        : ''
                                }`}
                                onClick={() => setPanel(item.panel)}
                            >
                                {item.label}
                            </button>
                        ))}

                        <button
                            type="button"
                            className="admin-nav-btn admin-nav-btn--danger"
                            onClick={handleLogOut}
                        >
                            Log out
                        </button>
                    </nav>

                    <p className="admin-dashboard-footnote">
                        <Link to="/" className="admin-dashboard-store-link">
                            View storefront
                        </Link>
                    </p>
                </aside>

                <div className="admin-dashboard-main">
                    {active === null && <LandingPage />}
                    {active === 0 && <ProductForm />}
                    {active === 1 && <InquiryList />}
                    {active === 2 && <OtherActions />}
                    {active === 6 && <Profile />}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Dashboard;
